import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import CampaignModal from 'components/CampaignModal';

import {
  canShowIntentBanner,
  markIntentBannerCtaClicked,
  markIntentBannerDismissed,
  markIntentBannerShown,
} from 'lib/intentBannerFrequency';
import { sendSupixEvent } from 'lib/supix';

import { useUser } from 'providers/UserProvider';

import useAdBanner from './useAdBanner';
import useExitIntent from './useExitIntent';
import useFirstIntent from './useFirstIntent';
import useMediaQuery from './useMediaQuery';
import useModal from './useModal';

import INTENT_BANNER, { INTENT_TYPES } from 'constants/intentBanner';

const supixFor = (type) =>
  type === INTENT_TYPES.EXIT
    ? INTENT_BANNER.SUPIX.EXIT
    : INTENT_BANNER.SUPIX.FIRST;

const fetcherFor = async (type) => {
  // Lazy import keeps the /ads intent fetchers out of the boot/critical path.
  const { getFirstIntentAds, getExitIntentAds } = await import('services/ads');

  return type === INTENT_TYPES.EXIT ? getExitIntentAds() : getFirstIntentAds();
};

/**
 * Map a single (camelCased) /ads item to the CampaignModal `campaigns[]`
 * contract, resolving image and CTA destination by device.
 */
// Only allow http(s) or root-relative hrefs — neutralizes javascript:/data: in
// the admin-controlled CTA url (stored XSS). Unsafe values become undefined so
// the CTA renders without a dangerous href.
const safeHref = (url) =>
  typeof url === 'string' &&
  (/^https?:\/\//i.test(url) || url.startsWith('/')) &&
  !url.startsWith('//') // reject protocol-relative (//evil.com open redirect)
    ? url
    : undefined;

export const mapAdToCampaign = (ad, { isMobile } = {}) => {
  const content = ad?.smartbannerObj?.content || {};

  // Sanitize each candidate independently so an unsafe per-device url falls
  // back to the (safe) banner url instead of dropping the CTA entirely.
  const ctaUrl =
    safeHref(isMobile ? content.ctaUrlMobile : content.ctaUrlDesktop) ||
    safeHref(ad?.url);

  return {
    id: ad?.id,
    imageUrl: isMobile ? ad?.mobileImage : ad?.image,
    title: content.title,
    description: content.description,
    ctaText: content.ctaText,
    ctaUrl,
    ctaTarget: content.ctaTarget || '_blank',
    utmCampaign: content.utmCampaign || ad?.utmCampaign,
  };
};

/**
 * Orchestrates an intent banner (first or exit): fetches on demand, filters by
 * configured visibility, maps to the CampaignModal contract, fires the trigger
 * hook, enforces the frequency cap and emits Supix 0006 events.
 *
 * @param {('first'|'exit')} type
 */
const useIntentBanner = (type) => {
  const { showModal } = useModal();
  const { isAdBannerVisible } = useAdBanner();
  const { isMobile } = useMediaQuery();
  const { LoggedInUser } = useUser();
  const [shouldFetch, setShouldFetch] = useState(false);

  const supix = supixFor(type);
  const userId = LoggedInUser?.userId;

  // Lazy fetch: armed after mount, kept off the boot/critical path.
  const { data: ads = [] } = useQuery({
    queryKey: ['intent-banner', type],
    queryFn: () => fetcherFor(type),
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000,
  });

  // Visibility filter (ALL/LOGGED/LOGGED_OUT via cookie; no hardcoded state).
  const visibleAds = useMemo(
    () => (Array.isArray(ads) ? ads.filter(isAdBannerVisible) : []),
    [ads, isAdBannerVisible]
  );

  const campaigns = useMemo(
    () => visibleAds.map((ad) => mapAdToCampaign(ad, { isMobile })),
    [visibleAds, isMobile]
  );

  // Lead campaign drives the frequency cap (the slider shows the whole array).
  const lead = campaigns[0];
  // Memoized so a benign re-render doesn't hand the trigger hooks a fresh
  // `config` reference — that would re-run their effect and reset the dwell
  // timer (mountedAt). A genuine campaign change (new visibleAds) still updates
  // it, which is correct: a new campaign should restart the dwell window.
  const intentConfig = useMemo(
    () => visibleAds[0]?.smartbannerObj?.intent || {},
    [visibleAds]
  );

  const sendEvent = useCallback(
    (category, campaign) => {
      sendSupixEvent('/car', {
        category,
        ...(userId ? { user_id: userId } : {}),
        ...(campaign?.utmCampaign
          ? { utm_campaign: campaign.utmCampaign }
          : {}),
      });
    },
    [userId]
  );

  // Gate by the presence of an active intent banner (admin-scheduled via /ads)
  // + the frequency cap. No build-time env flag — toggling is operational.
  const triggerEnabled =
    !!lead &&
    canShowIntentBanner({
      type,
      campaignId: lead.id,
      frequency: intentConfig.frequency,
    });

  const handleTrigger = useCallback(() => {
    if (!lead) {
      return;
    }

    markIntentBannerShown({ type, campaignId: lead.id });
    sendEvent(supix.VIEW, lead);

    showModal(CampaignModal, {
      campaigns,
      onClose: () => {
        markIntentBannerDismissed({ type, campaignId: lead.id });
        sendEvent(supix.DISMISS, lead);
      },
      onCtaClick: () => {
        markIntentBannerCtaClicked({ campaignId: lead.id });
        sendEvent(supix.CTA, lead);
      },
    });
  }, [lead, campaigns, type, supix, sendEvent, showModal]);

  // Arm the lazy fetch after mount (post first paint). Kept off the boot/
  // critical path so it never impacts FCP/LCP. A microtask defer
  // (Promise.resolve) pushes it past the initial render commit.
  useEffect(() => {
    let active = true;

    Promise.resolve().then(() => {
      if (active) {
        setShouldFetch(true);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  useFirstIntent({
    enabled: type === INTENT_TYPES.FIRST && triggerEnabled,
    isMobile,
    onTrigger: handleTrigger,
    config: intentConfig.first,
  });

  useExitIntent({
    enabled: type === INTENT_TYPES.EXIT && triggerEnabled,
    isMobile,
    onTrigger: handleTrigger,
    config: intentConfig.exit,
  });

  return { campaigns, triggerEnabled };
};

export default useIntentBanner;
